import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, TextField, Stack, Autocomplete, Avatar, CircularProgress, MenuItem, Typography, IconButton, LinearProgress, Alert, Chip } from '@mui/material';
import { searchMembers } from '../../controllers/memberManagementController';
import { fetchAllGroups } from '../../controllers/groupManagementController';
import { createMember } from '../../controllers/memberManagementController';
import { resolveGroupsAssetUrl } from '../../api/httpClientGroups';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import MemberCreateConfirmDialog from '../../components/MemberCreateConfirmDialog';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

const CreateMemberDialog = ({ open, onClose, onCreated }) => {
  const [values, setValues] = useState({
    FirstName: '',
    LastName: '',
    Email: '',
    PhoneNumber: '',
    JobTitle: '',
    Company: '',
    Sector: '',
    University: '',
    HighSchool: '',
    Address: '',
    WorkAddress: '',
    TcKimlikNo: '',
    BirthDate: '',
    ClubJoinDate: '',
    Website: '',
    Facebook: '',
    Twitter: '',
    LinkedIn: '',
    LogoCariCode: '',
    Password: '',
  });
  const [membershipType, setMembershipType] = useState(0); // 0 = Regular (Devamlı Üye)
  const [groupId, setGroupId] = useState('');
  const [reference, setReference] = useState(null);
  const [refQuery, setRefQuery] = useState('');
  const [refLoading, setRefLoading] = useState(false);
  const [refOptions, setRefOptions] = useState([]);
  const [groups, setGroups] = useState([]);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [validationError, setValidationError] = useState('');

  // Children to create along with member
  const [pendingChildren, setPendingChildren] = useState([]);
  const [childDraft, setChildDraft] = useState({ firstName: '', lastName: '', birthDate: '' });

  // Hobiler: liste olarak yönet, Enter ile ekle, çarpı ile sil, backend'e virgülle gönder
  const [hobbyList, setHobbyList] = useState([]);
  const [hobbyInput, setHobbyInput] = useState('');

  useEffect(() => {
    if (!open) return;
    let active = true;
    (async () => {
      try {
        const list = await fetchAllGroups();
        if (active) setGroups(list);
      } catch (e) {}
    })();
    return () => { active = false; };
  }, [open]);

  useEffect(() => {
    if (file) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setPreviewUrl('');
  }, [file]);

  useEffect(() => {
    if (!refQuery || refQuery.length < 2) { setRefOptions([]); return; }
    let active = true;
    setRefLoading(true);
    searchMembers(refQuery)
      .then((data) => { if (active) setRefOptions(data || []); })
      .catch(() => { if (active) setRefOptions([]); })
      .finally(() => { if (active) setRefLoading(false); });
    return () => { active = false; };
  }, [refQuery]);

  const handleChange = (key) => (e) => {
    setValues((v) => ({ ...v, [key]: e.target.value }));
    // Hata varsa temizle
    if (errors[key]) {
      setErrors((prev) => ({ ...prev, [key]: '' }));
    }
    // Validation error'u temizle
    if (validationError) {
      setValidationError('');
    }
  };

  const reset = () => {
    setValues({
      FirstName: '',
      LastName: '',
      Email: '',
      PhoneNumber: '',
      JobTitle: '',
      Company: '',
      Sector: '',
      University: '',
      HighSchool: '',
      Address: '',
      WorkAddress: '',
      TcKimlikNo: '',
      BirthDate: '',
      ClubJoinDate: '',
      Website: '',
      Facebook: '',
      Twitter: '',
      LinkedIn: '',
      LogoCariCode: '',
      Password: '',
    });
    setMembershipType(0); // Reset to Regular
    setGroupId('');
    setReference(null);
    setRefQuery('');
    setRefOptions([]);
    setFile(null);
    setLoading(false);
    setCurrentStep(1);
    setErrors({});
    setValidationError('');
    setPendingChildren([]);
    setChildDraft({ firstName: '', lastName: '', birthDate: '' });
    setHobbyList([]);
    setHobbyInput('');
  };

  const handleClose = () => {
    reset();
    onClose?.();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setConfirmOpen(true);
  };

  const handleConfirmSubmit = async () => {
    setLoading(true);
    try {
      // Telefon numarasının başında + işareti olduğundan emin ol
      const phoneNumber = values.PhoneNumber && !values.PhoneNumber.startsWith('+') 
        ? `+${values.PhoneNumber}` 
        : values.PhoneNumber;
      
      const payload = {
        FirstName: values.FirstName?.trim(),
        LastName: values.LastName?.trim(),
        Email: values.Email,
        PhoneNumber: phoneNumber,
        JobTitle: values.JobTitle,
        Company: values.Company,
        Sector: values.Sector,
        University: values.University,
        HighSchool: values.HighSchool,
        Address: values.Address,
        WorkAddress: values.WorkAddress,
        TcKimlikNo: values.TcKimlikNo || undefined,
        BirthDate: values.BirthDate ? new Date(values.BirthDate).toISOString() : undefined,
        ClubJoinDate: values.ClubJoinDate ? new Date(values.ClubJoinDate).toISOString() : undefined,
        Website: values.Website?.trim() || undefined,
        Hobbies: hobbyList.length ? hobbyList.join(', ') : undefined,
        Facebook: values.Facebook?.trim() || undefined,
        Twitter: values.Twitter?.trim() || undefined,
        LinkedIn: values.LinkedIn?.trim() || undefined,
        LogoCariCode: values.LogoCariCode?.trim() || undefined,
        GroupId: groupId || undefined,
        ReferenceMemberId: reference?.id || undefined,
        Password: values.Password,
        ProfilePicture: file || undefined,
        MembershipType: membershipType,
      };
      const created = await createMember(payload);

      // Çocuklar varsa, create sonrası tek tek ekle
      if (pendingChildren.length > 0 && created?.id) {
        try {
          const { createMemberChild } = await import('../../controllers/memberManagementController');
          // Sıralı olarak ekle (hata olsa bile diğerlerine devam etmemiz gerekmiyor, basit tutuyoruz)
          // Gerekirse Promise.all ile paralel de yapılabilir.
          // Burada kritik değil, çocuk sayısı genelde az.
          // eslint-disable-next-line no-restricted-syntax
          for (const c of pendingChildren) {
            // skip invalid
            if (!c.firstName || !c.lastName) continue;
            // eslint-disable-next-line no-await-in-loop
            await createMemberChild({
              memberId: created.id,
              firstName: c.firstName,
              lastName: c.lastName,
              birthDate: c.birthDate ? new Date(c.birthDate).toISOString() : null,
            });
          }
        } catch (e) {
          // Çocuk ekleme başarısız olsa da ana üye başarıyla oluşturulmuş oluyor
          // Gerekirse burada toast ile uyarı gösterilebilir.
        }
      }

      onCreated?.(created);
      setConfirmOpen(false);
      handleClose();
    } catch (err) {
      setLoading(false);
    }
  };


  const steps = ['Kişisel Bilgiler', 'Eğitim Bilgileri', 'Çocuklar', 'Kulüp Bilgileri'];

  const handleNext = () => {
    if (currentStep < 4) {
      if (validateStep(currentStep)) {
        setValidationError('');
        setCurrentStep(currentStep + 1);
      } else {
        setValidationError('Lütfen tüm zorunlu alanları doğru şekilde doldurunuz.');
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const validateStep = (step) => {
    const newErrors = {};
    let isValid = true;

    if (step === 1) {
      // Kişisel Bilgiler validasyonu
      if (!values.FirstName?.trim()) {
        newErrors.FirstName = 'Ad alanı zorunludur';
        isValid = false;
      }
      if (!values.LastName?.trim()) {
        newErrors.LastName = 'Soyad alanı zorunludur';
        isValid = false;
      }
      if (!values.Email?.trim()) {
        newErrors.Email = 'E-posta alanı zorunludur';
        isValid = false;
      } else {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(values.Email.trim())) {
          newErrors.Email = 'Geçerli bir e-posta adresi giriniz';
          isValid = false;
        }
      }
      if (!values.PhoneNumber || values.PhoneNumber.length < 8) {
        newErrors.PhoneNumber = 'Geçerli bir telefon numarası giriniz';
        isValid = false;
      }
    } else if (step === 4) {
      // Kulüp Bilgileri validasyonu
      if (!groupId) {
        newErrors.groupId = 'Grup seçimi zorunludur';
        isValid = false;
      }
      if (!values.Password?.trim()) {
        newErrors.Password = 'Geçici şifre alanı zorunludur';
        isValid = false;
      } else if (values.Password.trim().length < 6) {
        newErrors.Password = 'Şifre en az 6 karakter olmalıdır';
        isValid = false;
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  const canProceedToNextStep = () => {
    if (currentStep === 1) {
      return values.FirstName && values.LastName && values.Email && values.PhoneNumber;
    }
    if (currentStep === 2) {
      return true; // Eğitim bilgileri opsiyonel
    }
    if (currentStep === 3) {
      return true; // Çocuklar opsiyonel
    }
    return false;
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle
        sx={{
          fontSize: '18px',
          fontWeight: 500,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pr: 3,
        }}
      >
        Yeni Üye
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <Box sx={{ px: 3, pt: 2, pb: 2 }}>
        <Box sx={{ position: 'relative', mb: 1 }}>
          <LinearProgress 
            variant="determinate" 
            value={(currentStep / 4) * 100} 
            sx={{
              height: 4,
              borderRadius: '2px',
              backgroundColor: '#E7EDF4',
              '& .MuiLinearProgress-bar': {
                backgroundColor: '#0E4D94',
                borderRadius: '2px',
              },
            }}
          />
        </Box>
        <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
          {steps.map((label, index) => (
            <Box key={label} sx={{ flex: 1, textAlign: 'center' }}>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: currentStep === index + 1 ? 600 : 400,
                  color: currentStep === index + 1 ? '#0E4D94' : currentStep > index + 1 ? '#0E4D94' : '#a3a3a3',
                }}
              >
                {label}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Box>
      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ py: 2, px: 3 }}>
          {validationError && (
            <Alert severity="error" sx={{ mb: 2, fontSize: '13px' }} onClose={() => setValidationError('')}>
              {validationError}
            </Alert>
          )}
          <Stack spacing={2}>
            {/* Aşama 1: Kişisel Bilgiler */}
            {currentStep === 1 && (
              <>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="body2" sx={{ fontWeight: 500, width: '25%', flexShrink: 0 }}>
                    Ad
                  </Typography>
                  <TextField
                    fullWidth
                    value={values.FirstName}
                    onChange={handleChange('FirstName')}
                    required
                    placeholder="Ad giriniz"
                    sx={{
                      flex: '0 0 75%',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '5px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0E4D94',
                    },
                  },
                  '& .MuiInputBase-input': {
                    py: 1.2,
                    px: 1.5,
                    fontSize: '14px',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e6e6e6',
                  },
                }}
              />
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="body2" sx={{ fontWeight: 500, width: '25%', flexShrink: 0 }}>
                Soyad
              </Typography>
                  <TextField
                    fullWidth
                    value={values.LastName}
                    onChange={handleChange('LastName')}
                    required
                    placeholder="Soyad giriniz"
                    error={!!errors.LastName}
                    helperText={errors.LastName}
                    sx={{
                      flex: '0 0 75%',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '5px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0E4D94',
                    },
                  },
                  '& .MuiInputBase-input': {
                    py: 1.2,
                    px: 1.5,
                    fontSize: '14px',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e6e6e6',
                  },
                }}
              />
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="body2" sx={{ fontWeight: 500, width: '25%', flexShrink: 0 }}>
                E-posta
              </Typography>
                  <TextField
                    fullWidth
                    value={values.Email}
                    onChange={handleChange('Email')}
                    type="email"
                    required
                    placeholder="E-posta giriniz"
                    error={!!errors.Email}
                    helperText={errors.Email}
                    sx={{
                      flex: '0 0 75%',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '5px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0E4D94',
                    },
                  },
                  '& .MuiInputBase-input': {
                    py: 1.2,
                    px: 1.5,
                    fontSize: '14px',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e6e6e6',
                  },
                }}
              />
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="body2" sx={{ fontWeight: 500, width: '25%', flexShrink: 0 }}>
                Telefon
              </Typography>
              <Box sx={{ flex: '0 0 75%' }}>
                <PhoneInput
                  country={'tr'}
                  value={values.PhoneNumber}
                  onChange={(value) => {
                    setValues((v) => ({ ...v, PhoneNumber: value }));
                    if (errors.PhoneNumber) {
                      setErrors((prev) => ({ ...prev, PhoneNumber: '' }));
                    }
                    if (validationError) {
                      setValidationError('');
                    }
                  }}
                  inputStyle={{
                    width: '100%',
                    height: '42px',
                    fontSize: '14px',
                    borderRadius: '5px',
                    borderColor: errors.PhoneNumber ? '#d32f2f' : '#e6e6e6',
                  }}
                  buttonStyle={{
                    borderRadius: '5px 0 0 5px',
                    borderColor: errors.PhoneNumber ? '#d32f2f' : '#e6e6e6',
                  }}
                  containerStyle={{
                    width: '100%',
                  }}
                />
                {errors.PhoneNumber && (
                  <Typography variant="caption" sx={{ color: '#d32f2f', fontSize: '12px', mt: 0.5, ml: 1.5 }}>
                    {errors.PhoneNumber}
                  </Typography>
                )}
              </Box>
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="body2" sx={{ fontWeight: 500, width: '25%', flexShrink: 0 }}>
                Ünvan
              </Typography>
              <TextField
                fullWidth
                value={values.JobTitle}
                onChange={handleChange('JobTitle')}
                placeholder="Ünvan giriniz"
                sx={{
                  flex: '0 0 75%',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '5px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0E4D94',
                    },
                  },
                  '& .MuiInputBase-input': {
                    py: 1.2,
                    px: 1.5,
                    fontSize: '14px',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e6e6e6',
                  },
                }}
              />
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="body2" sx={{ fontWeight: 500, width: '25%', flexShrink: 0 }}>
                Şirket
              </Typography>
              <TextField
                fullWidth
                value={values.Company}
                onChange={handleChange('Company')}
                placeholder="Şirket giriniz"
                sx={{
                  flex: '0 0 75%',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '5px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0E4D94',
                    },
                  },
                  '& .MuiInputBase-input': {
                    py: 1.2,
                    px: 1.5,
                    fontSize: '14px',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e6e6e6',
                  },
                }}
              />
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="body2" sx={{ fontWeight: 500, width: '25%', flexShrink: 0 }}>
                Sektör
              </Typography>
              <TextField
                fullWidth
                value={values.Sector}
                onChange={handleChange('Sector')}
                placeholder="Sektör giriniz"
                sx={{
                  flex: '0 0 75%',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '5px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0E4D94',
                    },
                  },
                  '& .MuiInputBase-input': {
                    py: 1.2,
                    px: 1.5,
                    fontSize: '14px',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e6e6e6',
                  },
                }}
              />
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="body2" sx={{ fontWeight: 500, width: '25%', flexShrink: 0 }}>
                Logo Cari Kod
              </Typography>
              <TextField
                fullWidth
                value={values.LogoCariCode}
                onChange={handleChange('LogoCariCode')}
                placeholder="Logo Bulut cari hesap kodu"
                inputProps={{ maxLength: 60 }}
                sx={{
                  flex: '0 0 75%',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '5px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0E4D94',
                    },
                  },
                  '& .MuiInputBase-input': {
                    py: 1.2,
                    px: 1.5,
                    fontSize: '14px',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e6e6e6',
                  },
                }}
              />
            </Stack>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <Typography variant="body2" sx={{ fontWeight: 500, width: '25%', flexShrink: 0, pt: 1.5 }}>
                    Ev Adresi
                  </Typography>
                  <TextField
                    fullWidth
                    value={values.Address}
                    onChange={handleChange('Address')}
                    multiline
                    rows={2}
                    placeholder="Ev adresi giriniz"
                    sx={{
                      flex: '0 0 75%',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '5px',
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#0E4D94',
                        },
                      },
                      '& .MuiInputBase-input': {
                        
                        fontSize: '14px',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#e6e6e6',
                      },
                    }}
                  />
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="body2" sx={{ fontWeight: 500, width: '25%', flexShrink: 0 }}>
                    Web Sitesi
                  </Typography>
                  <TextField
                    fullWidth
                    value={values.Website}
                    onChange={handleChange('Website')}
                    placeholder="https://..."
                    sx={{
                      flex: '0 0 75%',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '5px',
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#0E4D94' },
                      },
                      '& .MuiInputBase-input': { py: 1.2, px: 1.5, fontSize: '14px' },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e6e6e6' },
                    }}
                  />
                </Stack>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <Typography variant="body2" sx={{ fontWeight: 500, width: '25%', flexShrink: 0, pt: 1.5 }}>
                    Hobiler
                  </Typography>
                  <Box sx={{ flex: '0 0 75%' }}>
                    <TextField
                      fullWidth
                      value={hobbyInput}
                      onChange={(e) => setHobbyInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const v = hobbyInput.trim();
                          if (v && !hobbyList.includes(v)) {
                            setHobbyList((prev) => [...prev, v]);
                            setHobbyInput('');
                          }
                        }
                      }}
                      placeholder="Hobi yazıp Enter'a basın"
                      helperText="Hobi adını yazıp 'Enter' tuşu ile ekleyin. Listedeki çarpı tuşu ile listeden çıkarın."
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '5px',
                          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#0E4D94' },
                        },
                        '& .MuiInputBase-input': { py: 1.2, px: 1.5, fontSize: '14px' },
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e6e6e6' },
                        '& .MuiFormHelperText-root': { mx: 0 },
                      }}
                    />
                    {hobbyList.length > 0 && (
                      <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 1 }}>
                        {hobbyList.map((h) => (
                          <Chip
                            key={h}
                            label={h}
                            size="small"
                            onDelete={() => setHobbyList((prev) => prev.filter((x) => x !== h))}
                            sx={{ fontSize: '12px' }}
                          />
                        ))}
                      </Stack>
                    )}
                  </Box>
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="body2" sx={{ fontWeight: 500, width: '25%', flexShrink: 0 }}>
                    Facebook
                  </Typography>
                  <TextField
                    fullWidth
                    value={values.Facebook}
                    onChange={handleChange('Facebook')}
                    placeholder="URL"
                    sx={{
                      flex: '0 0 75%',
                      '& .MuiOutlinedInput-root': { borderRadius: '5px', '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#0E4D94' } },
                      '& .MuiInputBase-input': { py: 1.2, px: 1.5, fontSize: '14px' },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e6e6e6' },
                    }}
                  />
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="body2" sx={{ fontWeight: 500, width: '25%', flexShrink: 0 }}>
                    Twitter
                  </Typography>
                  <TextField
                    fullWidth
                    value={values.Twitter}
                    onChange={handleChange('Twitter')}
                    placeholder="URL"
                    sx={{
                      flex: '0 0 75%',
                      '& .MuiOutlinedInput-root': { borderRadius: '5px', '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#0E4D94' } },
                      '& .MuiInputBase-input': { py: 1.2, px: 1.5, fontSize: '14px' },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e6e6e6' },
                    }}
                  />
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="body2" sx={{ fontWeight: 500, width: '25%', flexShrink: 0 }}>
                    LinkedIn
                  </Typography>
                  <TextField
                    fullWidth
                    value={values.LinkedIn}
                    onChange={handleChange('LinkedIn')}
                    placeholder="URL"
                    sx={{
                      flex: '0 0 75%',
                      '& .MuiOutlinedInput-root': { borderRadius: '5px', '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#0E4D94' } },
                      '& .MuiInputBase-input': { py: 1.2, px: 1.5, fontSize: '14px' },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e6e6e6' },
                    }}
                  />
                </Stack>
              </>
            )}

            {/* Aşama 2: Eğitim Bilgileri */}
            {currentStep === 2 && (
              <>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="body2" sx={{ fontWeight: 500, width: '25%', flexShrink: 0 }}>
                    Lise
                  </Typography>
                  <TextField
                    fullWidth
                    value={values.HighSchool}
                    onChange={handleChange('HighSchool')}
                    placeholder="Lise giriniz"
                    sx={{
                      flex: '0 0 75%',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '5px',
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#0E4D94',
                        },
                      },
                      '& .MuiInputBase-input': {
                        py: 1.2,
                        px: 1.5,
                        fontSize: '14px',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#e6e6e6',
                      },
                    }}
                  />
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="body2" sx={{ fontWeight: 500, width: '25%', flexShrink: 0 }}>
                    Üniversite
                  </Typography>
                  <TextField
                    fullWidth
                    value={values.University}
                    onChange={handleChange('University')}
                    placeholder="Üniversite giriniz"
                    sx={{
                      flex: '0 0 75%',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '5px',
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#0E4D94',
                        },
                      },
                      '& .MuiInputBase-input': {
                        py: 1.2,
                        px: 1.5,
                        fontSize: '14px',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#e6e6e6',
                      },
                    }}
                  />
                </Stack>
              </>
            )}

            {/* Aşama 3: Çocuklar */}
            {currentStep === 3 && (
              <>
                <Typography variant="body2" sx={{ fontWeight: 500, color: '#a3a3a3', mb: 0.5 }}>
                  Çocuklar (opsiyonel)
                </Typography>
                <Stack spacing={2}>
                  <Box
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#444', mb: 1.5, fontSize: '13px' }}>
                      Yeni çocuk ekle
                    </Typography>
                    <Stack spacing={2}>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Typography variant="body2" sx={{ fontWeight: 500, width: '25%', flexShrink: 0 }}>
                          Ad
                        </Typography>
                        <TextField
                          fullWidth
                          value={childDraft.firstName}
                          onChange={(e) => setChildDraft((c) => ({ ...c, firstName: e.target.value }))}
                          placeholder="Çocuğun adı"
                          size="small"
                          sx={{
                            flex: '0 0 75%',
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '5px',
                              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#0E4D94' },
                            },
                            '& .MuiInputBase-input': { py: 1, px: 1.5, fontSize: '14px' },
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e6e6e6' },
                          }}
                        />
                      </Stack>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Typography variant="body2" sx={{ fontWeight: 500, width: '25%', flexShrink: 0 }}>
                          Soyad
                        </Typography>
                        <TextField
                          fullWidth
                          value={childDraft.lastName}
                          onChange={(e) => setChildDraft((c) => ({ ...c, lastName: e.target.value }))}
                          placeholder="Çocuğun soyadı"
                          size="small"
                          sx={{
                            flex: '0 0 75%',
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '5px',
                              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#0E4D94' },
                            },
                            '& .MuiInputBase-input': { py: 1, px: 1.5, fontSize: '14px' },
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e6e6e6' },
                          }}
                        />
                      </Stack>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Typography variant="body2" sx={{ fontWeight: 500, width: '25%', flexShrink: 0 }}>
                          Doğum Tarihi
                        </Typography>
                        <Box sx={{ flex: '0 0 75%', display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                          <TextField
                            type="date"
                            size="small"
                            value={childDraft.birthDate}
                            onChange={(e) => setChildDraft((c) => ({ ...c, birthDate: e.target.value }))}
                            InputLabelProps={{ shrink: true }}
                            sx={{
                              flex: '1 1 180px',
                              minWidth: 0,
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '5px',
                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#0E4D94' },
                              },
                              '& .MuiInputBase-input': { py: 1, px: 1.5, fontSize: '14px' },
                              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e6e6e6' },
                            }}
                          />
                          <Button
                            type="button"
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => {
                              if (!childDraft.firstName.trim() || !childDraft.lastName.trim()) return;
                              setPendingChildren((list) => [
                                ...list,
                                {
                                  tempId: Date.now(),
                                  firstName: childDraft.firstName.trim(),
                                  lastName: childDraft.lastName.trim(),
                                  birthDate: childDraft.birthDate || '',
                                },
                              ]);
                              setChildDraft({ firstName: '', lastName: '', birthDate: '' });
                            }}
                            disabled={!childDraft.firstName.trim() || !childDraft.lastName.trim()}
                            sx={{
                              textTransform: 'none',
                              fontWeight: 600,
                              borderRadius: '5px',
                              backgroundColor: '#0E4D94',
                              boxShadow: 'none',
                              '&:hover': { backgroundColor: '#0a3a70', boxShadow: 'none' },
                              '&.Mui-disabled': { backgroundColor: '#e0e0e0', color: '#9e9e9e' },
                            }}
                          >
                            Çocuk ekle
                          </Button>
                        </Box>
                      </Stack>
                    </Stack>
                    <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#a3a3a3', fontSize: '12px' }}>
                      Ad ve soyad zorunludur. Doğum tarihi isteğe bağlıdır. Ekledikten sonra listeden çarpı ile silebilirsiniz.
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Typography variant="body2" sx={{ fontWeight: 500, width: '25%', flexShrink: 0, pt: 1.5 }}>
                      Eklenen çocuklar
                      {pendingChildren.length > 0 && (
                        <Typography component="span" variant="body2" sx={{ color: '#0E4D94', fontWeight: 600, ml: 0.5 }}>
                          ({pendingChildren.length})
                        </Typography>
                      )}
                    </Typography>
                    <Box
                      sx={{
                        flex: '0 0 75%',
                        border: '1px solid #e6e6e6',
                        borderRadius: '8px',
                        minHeight: 56,
                      }}
                    >
                      {pendingChildren.length === 0 ? (
                        <Box sx={{ py: 2, px: 2, textAlign: 'center' }}>
                          <Typography variant="body2" sx={{ color: '#a3a3a3', fontSize: '13px' }}>
                            Henüz çocuk eklenmedi.
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#a3a3a3', display: 'block', mt: 0.25 }}>
                            Yukarıdaki alanları doldurup &quot;Çocuk ekle&quot; butonuna tıklayın.
                          </Typography>
                        </Box>
                      ) : (
                        <Stack direction="row" flexWrap="wrap" gap={1} sx={{ p: 1.5 }}>
                          {pendingChildren.map((c) => (
                            <Chip
                              key={c.tempId}
                              label={
                                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                  <Typography component="span" variant="body2" sx={{ fontWeight: 500, fontSize: '13px' }}>
                                    {`${c.firstName} ${c.lastName}`.trim()}
                                  </Typography>
                                  {c.birthDate && (
                                    <Typography component="span" variant="caption" sx={{ color: '#a3a3a3' }}>
                                      • {new Date(c.birthDate).toLocaleDateString('tr-TR')}
                                    </Typography>
                                  )}
                                </Box>
                              }
                              onDelete={() => setPendingChildren((list) => list.filter((x) => x.tempId !== c.tempId))}
                              size="medium"
                              sx={{
                                borderRadius: '6px',
                                border: '1px solid #e6e6e6',
                                '& .MuiChip-deleteIcon': {
                                  color: '#666',
                                  '&:hover': { color: '#0E4D94' },
                                },
                              }}
                            />
                          ))}
                        </Stack>
                      )}
                    </Box>
                  </Stack>
                </Stack>
              </>
            )}

            {/* Aşama 4: Kulüp Bilgileri */}
            {currentStep === 4 && (
              <>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="body2" sx={{ fontWeight: 500, width: '25%', flexShrink: 0 }}>
                    Referans Üye
                  </Typography>
                  <Box sx={{ flex: '0 0 75%' }}>
              <Autocomplete
                options={refOptions}
                loading={refLoading}
                getOptionLabel={(o) => o?.fullName || ''}
                value={reference}
                onChange={(e, v) => setReference(v)}
                onInputChange={(e, v) => setRefQuery(v)}
                filterOptions={(x) => x}
                isOptionEqualToValue={(a, b) => a?.id === b?.id}
                noOptionsText="Sonuç yok"
                loadingText="Aranıyor..."
                fullWidth
                renderOption={(props, option) => (
                  <Stack component="li" direction="row" spacing={1} alignItems="center" {...props} key={option.id}>
                    <Avatar src={resolveGroupsAssetUrl(option.profilePictureUrl)} sx={{ width: 24, height: 24 }} />
                    {option.fullName}
                  </Stack>
                )}
                renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="İsim yazın..."
                      InputProps={{ 
                        ...params.InputProps, 
                        endAdornment: (<>{refLoading ? <CircularProgress color="inherit" size={16} /> : null}{params.InputProps.endAdornment}</>) 
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '5px',
                          py: 0,
                          px: 0,
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#0E4D94',
                          },
                        },
                        '& .MuiAutocomplete-inputRoot': {
                          py: '2.5px',
                          px: '12px',
                        },
                        '& .MuiAutocomplete-input': {
                          py: 0,
                          px: 0,
                          fontSize: '14px',
                        },
                        '& .MuiInputBase-input': {
                          py: 1.2,
                          px: 1.5,
                          fontSize: '14px',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#e6e6e6',
                        },
                      }}
                    />
                  )}
                      />
                    </Box>
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="body2" sx={{ fontWeight: 500, width: '25%', flexShrink: 0 }}>
                    Grup
                  </Typography>
                  <TextField
                    fullWidth
                    select
                    value={groupId}
                    onChange={(e) => {
                      setGroupId(e.target.value);
                      if (errors.groupId) {
                        setErrors((prev) => ({ ...prev, groupId: '' }));
                      }
                    }}
                    placeholder="Grup seçiniz"
                    error={!!errors.groupId}
                    helperText={errors.groupId}
                    sx={{
                      flex: '0 0 75%',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '5px',
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#0E4D94',
                        },
                      },
                      '& .MuiInputBase-input': {
                        py: 1.2,
                        px: 1.5,
                        fontSize: '14px',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#e6e6e6',
                      },
                    }}
                  >
                <MenuItem value="">Seçim yok</MenuItem>
                {groups.map((g) => (
                  <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>
                ))}
              </TextField>
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="body2" sx={{ fontWeight: 500, width: '25%', flexShrink: 0 }}>
                    Kulübe Giriş Tarihi
                  </Typography>
                  <TextField
                    fullWidth
                    type="date"
                    value={values.ClubJoinDate}
                    onChange={handleChange('ClubJoinDate')}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      flex: '0 0 75%',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '5px',
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#0E4D94' },
                      },
                      '& .MuiInputBase-input': { py: 1.2, px: 1.5, fontSize: '14px' },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e6e6e6' },
                    }}
                  />
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="body2" sx={{ fontWeight: 500, width: '25%', flexShrink: 0 }}>
                    Üyelik Tipi
                  </Typography>
                  <TextField
                    fullWidth
                    select
                    value={membershipType}
                    onChange={(e) => setMembershipType(Number(e.target.value))}
                    required
                    sx={{
                      flex: '0 0 75%',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '5px',
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#0E4D94',
                        },
                      },
                      '& .MuiInputBase-input': {
                        py: 1.2,
                        px: 1.5,
                        fontSize: '14px',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#e6e6e6',
                      },
                    }}
                  >
                <MenuItem value={0}>Devamlı Üye</MenuItem>
                <MenuItem value={1}>Devamdan Muaf Üye</MenuItem>
                <MenuItem value={2}>Aidattan Muaf Üye</MenuItem>
                <MenuItem value={3}>Onursal Üye</MenuItem>
                <MenuItem value={4}>İzinli Üye</MenuItem>
              </TextField>
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="body2" sx={{ fontWeight: 500, width: '25%', flexShrink: 0 }}>
                    Profil Fotoğrafı
                  </Typography>
                  <Box sx={{ flex: '0 0 75%' }}>
              <Stack direction="row" spacing={2} alignItems="center">
                      <Button 
                        variant="outlined" 
                        component="label"
                        sx={{
                          textTransform: 'none',
                          fontSize: 13,
                          borderRadius: '5px',
                          borderColor: '#e6e6e6',
                          color: '#444444',
                          '&:hover': {
                            borderColor: '#0E4D94',
                            backgroundColor: '#E7EDF4',
                          }
                        }}
                      >
                        Yükle
                  <input type="file" accept="image/*" hidden onChange={(e) => setFile(e.target.files?.[0] || null)} />
                </Button>
                {previewUrl && <Avatar src={previewUrl} sx={{ width: 56, height: 56 }} />}
                {file && !previewUrl && <Typography variant="caption">{file.name}</Typography>}
              </Stack>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="body2" sx={{ fontWeight: 500, width: '25%', flexShrink: 0 }}>
                    Geçici Şifre
                  </Typography>
                  <TextField
                    fullWidth
                    value={values.Password}
                    onChange={handleChange('Password')}
                    type="password"
                    required
                    placeholder="Şifre giriniz"
                    error={!!errors.Password}
                    helperText={errors.Password}
                    sx={{
                      flex: '0 0 75%',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '5px',
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#0E4D94',
                        },
                      },
                      '& .MuiInputBase-input': {
                        py: 1.2,
                        px: 1.5,
                        fontSize: '14px',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#e6e6e6',
                      },
                    }}
                  />
                </Stack>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ py: 2, px: 3 }}>
          <Stack direction="row" spacing={1} sx={{ width: '100%', justifyContent: 'flex-end' }}>
            {currentStep > 1 && (
              <Button
                type="button"
                onClick={handleBack}
                disabled={loading}
                sx={{
                  textTransform: 'none',
                  color: '#0E4D94',
                }}
              >
                Geri
              </Button>
            )}
            {currentStep < 4 ? (
              <Button
                type="button"
                onClick={handleNext}
                disabled={loading || !canProceedToNextStep()}
                variant="contained"
                sx={{
                  borderRadius: '5px',
                  textTransform: 'none',
                  fontSize: '14px',
                  boxShadow: 'none',
                  '&:hover': {
                    boxShadow: 'none',
                  },
                }}
              >
                İleri
              </Button>
            ) : (
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                sx={{
                  borderRadius: '5px',
                  textTransform: 'none',
                  fontSize: '14px',
                  boxShadow: 'none',
                  '&:hover': {
                    boxShadow: 'none',
                  },
                }}
              >
                {loading ? 'Oluşturuluyor...' : 'Oluştur'}
              </Button>
            )}
          </Stack>
        </DialogActions>
      </form>

      {/* Onay Modalı */}
      <MemberCreateConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmSubmit}
        loading={loading}
        data={{
          FirstName: values.FirstName,
          LastName: values.LastName,
          Email: values.Email,
          PhoneNumber: values.PhoneNumber,
          JobTitle: values.JobTitle,
          Company: values.Company,
          Sector: values.Sector,
          LogoCariCode: values.LogoCariCode,
          University: values.University,
          HighSchool: values.HighSchool,
          Address: values.Address,
          groupId: groupId,
          reference: reference,
          membershipType: membershipType,
          previewUrl: previewUrl,
          file: file,
        }}
        groups={groups}
      />
    </Dialog>
  );
};

export default CreateMemberDialog;


